import { BAR_GAP, BAR_WIDTH } from "./scrub.js";

// Peaks are integers 0..100, evenly spaced across the clip's full duration.

// Display contrast: the clip's quietest this-fraction maps to 0.
export const CONTRAST_FLOOR_PERCENTILE = 0.05;
// Display contrast: >1 pushes mid values down so the loud peaks stand out.
export const CONTRAST_EXPONENT = 1.7;
// Clips whose loudest-minus-floor spread (0..100 scale) is below this are flat.
export const CONTRAST_MIN_SPREAD = 8;
// Bar height (0..1) a flat clip is drawn at, scaled by its loudness.
const FLAT_CLIP_HEIGHT = 0.35;

// Server peaks if present and well-formed, else undefined.
export function serverPeaks(value) {
  if (!Array.isArray(value) || value.length === 0) return undefined;
  return value.every((p) => typeof p === "number" && Number.isFinite(p))
    ? value
    : undefined;
}

// Max-pools a series down (or up, by repetition) to exactly `count` values, so
// a short transient still shows in a narrow wave.
export function resamplePeaks(peaks, count) {
  if (count <= 0 || peaks.length === 0) return [];
  const out = new Array(count);
  for (let i = 0; i < count; i++) {
    const start = Math.floor((i * peaks.length) / count);
    const end = Math.max(start + 1, Math.floor(((i + 1) * peaks.length) / count));
    let max = 0;
    for (let j = start; j < end && j < peaks.length; j++) {
      max = Math.max(max, peaks[j]);
    }
    out[i] = max;
  }
  return out;
}

// Display-only contrast for real peaks as bar heights 0..1. floor = the
// CONTRAST_FLOOR_PERCENTILE value, max = the loudest;
// h = clamp((p - floor) / (max - floor), 0, 1) ** CONTRAST_EXPONENT. A
// near-flat clip is drawn at a steady FLAT_CLIP_HEIGHT * (max / 100).
export function stretchPeaks(peaks) {
  if (peaks.length === 0) return [];
  const sorted = [...peaks].sort((a, b) => a - b);
  const max = sorted[sorted.length - 1];
  const floor = sorted[Math.floor((sorted.length - 1) * CONTRAST_FLOOR_PERCENTILE)];
  if (max - floor < CONTRAST_MIN_SPREAD) {
    const h = FLAT_CLIP_HEIGHT * Math.min(1, Math.max(0, max / 100));
    return peaks.map(() => h);
  }
  const range = max - floor;
  return peaks.map(
    (p) => Math.min(1, Math.max(0, (p - floor) / range)) ** CONTRAST_EXPONENT,
  );
}

// Deterministic decorative wave for a clip with no peaks (mulberry32), keyed on
// a stable string such as the recording id.
export function generateBars(seed, count) {
  let state = 0;
  for (let i = 0; i < seed.length; i++) {
    state = (state + seed.charCodeAt(i) * (i + 1) * 2654435761) >>> 0;
  }

  const random = () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  return Array.from({ length: count }, (_, i) => {
    // Taper the ends so the clip reads as a clip rather than a flat block.
    const edge = Math.sin((Math.PI * i) / Math.max(1, count - 1));
    return 0.15 + random() * 0.85 * (0.4 + 0.6 * edge);
  });
}

// Bar heights (0..1) for `count` bars: the real peaks when present, else the
// seeded wave.
export function barHeights(peaks, seed, count) {
  if (count <= 0) return [];
  return peaks
    ? stretchPeaks(resamplePeaks(peaks, count))
    : generateBars(seed, count);
}

// One SVG path of mirrored vertical strokes (round caps) centred on the row.
export function barsPath(heights, height) {
  const mid = height / 2;
  let d = "";
  for (let i = 0; i < heights.length; i++) {
    const half = Math.max(1, heights[i] * mid);
    const x = i * (BAR_WIDTH + BAR_GAP) + BAR_WIDTH / 2;
    d += `M${x} ${(mid - half).toFixed(1)}V${(mid + half).toFixed(1)}`;
  }
  return d;
}
